import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GroupCreatorService, groupCreatorServiceToken } from 'go-modules/group-creator/group-creator.service';
import { groupToken } from 'go-modules/models/group-dep/group.factory';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { NgxCourseService } from '@go-modules/services/course/course.service';
import { CourseFolder } from '@go-modules/services/course/interfaces/course-folder.interface';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { FolderListDataSource } from '@go-modules/services/folder-list-datasource/folder-list-datasource';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import type { ConfirmDialogData } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import dayjs from 'dayjs';

export interface AiTrialCourseSelectorResult {
	folderId: number;
}

@Component({
	selector: 'app-ai-trial-course-selector-dialog',
	templateUrl: './ai-trial-course-selector-dialog.component.html',
	styleUrls: ['./ai-trial-course-selector-dialog.component.scss'],
	standalone: false
})
export class AiTrialCourseSelectorDialogComponent implements OnInit {

	public form: UntypedFormGroup;
	public folders = [];

	public environmentVarsService: EnvironmentVarsService;
	public isCreating$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public createdFolder;

	constructor (
		public dialogRef: MatDialogRef<AiTrialCourseSelectorDialogComponent, AiTrialCourseSelectorResult | undefined>,
		private dialog: MatDialog,
		private folderListDataSource: FolderListDataSource,
		private ngxCourseService: NgxCourseService,
		private translate: TranslateService,
		@Inject(groupToken) private group,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(groupCreatorServiceToken) private GroupCreator: GroupCreatorService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public ngOnInit () {
		const selectedGroup = this.selectedService.getGroup();
		const selectedOrg = this.selectedService.getOrg();
		this.form = new UntypedFormGroup({
			folder: new UntypedFormControl(null, [Validators.required])
		});

		if (this.isLti()) {
			this.folders = [selectedGroup];
			this.form.controls.folder.setValue(selectedGroup.group_id);
		} else {
			this.ngxCourseService.getCourses(selectedOrg.group_id).subscribe({
				next: (response) => {
					this.folders = response.map((folder) => {
						return this.group.model(folder, true);
					}).filter((folder) => {
						return folder.end_date === null ||
							(!!folder.end_date && dayjs.utc(folder.end_date).isAfter(dayjs().utc()));
					});

					if (selectedGroup && this.folders.some((folder) => folder.group_id === selectedGroup.group_id)) {
						this.form.controls.folder.setValue(selectedGroup.group_id);
					}
				}
			});
		}

		this.dialogRef.afterClosed()
			.subscribe((result) => {
				if (this.createdFolder) {
					if (result) {
						this.folderListDataSource.addFolder(this.createdFolder);
					} else {
						this.ngxCourseService.archiveCourse(this.createdFolder.group_id).subscribe();
					}
				}
			});
	}

	public isLti (): boolean {
		return this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public isAboutToExpire (folder) {
		return !!folder.end_date && dayjs.utc(folder.end_date).diff(dayjs().utc(), 'days') < 60;
	}

	public getExpireDate (folder) {
		return this.translate.instant('ai-trial-course-selector-dialog_folder-expire', {
			date: dayjs(folder.end_date).format('MMMM D')
		});
	}

	public getSelectedFolder () {
		const selectedFolderId = this.form.get('folder').value;
		const folder = this.folders.find((item) => item.group_id === selectedFolderId);
		return folder;
	}

	public displayFolderName (): string {
		const folder = this.getSelectedFolder();
		return folder ? folder.name : '';
	}

	public startTrial () {
		this.form.markAllAsTouched();

		if (this.form.valid) {
			const folder = this.getSelectedFolder();

			if (this.isAboutToExpire(folder)) {
				const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
					data: {
						title: this.translate.instant('ai-trial-course-selector-confirm_title'),
						message: this.translate.instant('ai-trial-course-selector-confirm_message'),
						confirmText: this.translate.instant('ai-trial-course-selector-confirm_submit-btn')
					} as ConfirmDialogData
				});

				dialogRef.afterClosed()
					.subscribe((confirm) => {
						if (confirm) {
							this.dialogRef.close({ folderId: this.form.get('folder').value });
						}
					});
			} else {
				this.dialogRef.close({ folderId: this.form.get('folder').value });
			}
		}
	}

	public close () {
		this.dialogRef.close();
	}

	public openCreateCoursePanel (): void {
		this.isCreating$.next(true);
		const account = this.selectedService.getAccount();
		const userId = this.userService.currentUser.user_id;

		const accountModel = this.group.model(account);
		this.GroupCreator.createCourseFolder(
			accountModel,
			this.userService.currentUser,
			userId,
			false,
			true
		).then((folder: CourseFolder) => {
			this.createdFolder = this.group.model(folder);
			this.folders.push(this.createdFolder);
			this.form.controls.folder.setValue(this.createdFolder.group_id);
		}).finally(() => this.isCreating$.next(false));
	}
}
